// Cursor → find-references target resolution (Slices 24 + 25 + 26).
//
// Split out of `referenceProvider.ts` so BOTH reference surfaces share one
// dispatch: Monaco's native peek (`referenceProvider.ts`) and the explicit
// "Find All References in Tab" editor action (`findReferencesInTabAction.ts`).
//
// Dispatch order: column-binding first (alias-qualified or 2/3-part chain),
// then table/view binding via the OE-tree lookup, then routine binding via the
// same OE-tree lookup (Slice 9's `findRoutineBindingAtCursor`). Bare 1-part
// columns (no dotted prefix) are deferred — they need scope walking via the
// resolver service, mirroring the column go-to-def deferral.
//
// Cross-database targets: the identifier index is per (connection, database)
// on MSSQL/PostgreSQL, so a chain naming a database other than the tab's
// (`MandTob.dbo.ARD` typed from a `master` tab) must carry `MandTob` as the
// `ReferenceTarget.database` — otherwise the caller waits on / queries the
// wrong database's index. What routes is the database the TYPED name spells
// out, never the one a resolver fell back to: the OE-tree lookup keeps a
// "first same-named object anywhere in the tree" fallback for BARE names (it
// is what lets navigation work before the tab's own database subtree has been
// expanded), and following it would search a database the user never named.
// So: the typed database, else the tab's, and only on a surface with NO
// database bound at all does the resolved one stand in — there the
// alternative is an empty name that matches no index. A chain whose typed
// database or schema is absent from the tree resolves to no target at all
// (`go-to/oeTreeQualification.ts`).
//
// What the resolvers hand over as `explicitDatabase` is the CATALOG spelling
// of the database the typed name bound to, not the source spelling: the
// lifecycle keys one index — and PostgreSQL one pool — per database IDENTITY,
// so an unquoted `Sales.public.users` that matched the tree's `sales` must
// route at `sales` rather than open a second, case-different entry. A quoted
// `"Sales"` that matched a catalog `Sales` still routes at `Sales`.

import type { editor, Position } from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';

import { findColumnBindingAtCursor } from '../go-to/columnAtCursor';
import { findTableViewHitAtCursor } from '../go-to/identifierAtCursor';
import { findRoutineBindingAtCursor } from '../go-to/routineAtCursor';
import { buildAliasResolver } from '../go-to/bufferAliasResolver';
import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import type { IdentifierKind, QualifiedName } from '../types';

export type ReferenceProviderDeps = {
  readonly engineId: DatabaseEngineId;
  readonly getConnectionId?: () => string | null;
  readonly getDatabase?: () => string | null;
};

export type ReferenceTarget = {
  readonly connectionId: string;
  readonly database: string;
  readonly identifier: QualifiedName;
  readonly identifierKind: IdentifierKind;
  /** Host relation for a column target — labels the references tab. */
  readonly relation?: QualifiedName;
};

export type CursorModel = Pick<editor.ITextModel, 'getValue' | 'getOffsetAt'>;

/** Resolve what the cursor is pointing at, or `null` when nothing
 *  find-references understands sits there (or no connection is bound). */
export function findReferenceTargetAtCursor(
  deps: ReferenceProviderDeps,
  model: CursorModel,
  position: Position,
): ReferenceTarget | null {
  const connectionId = deps.getConnectionId?.() ?? null;
  if (!connectionId) return null;

  const source = model.getValue();
  const offset = model.getOffsetAt(position);
  const database = deps.getDatabase?.() ?? null;
  const tabDatabase = database ?? '';
  const base = { connectionId, database: tabDatabase };

  const columnHit = findColumnBindingAtCursor(source, offset, {
    engineId: deps.engineId,
    connectionId,
    database,
    resolveAlias: buildAliasResolver({
      source,
      cursorOffset: offset,
      engine: databaseEngineIdToSqlEngine(deps.engineId) ?? 'mssql',
    }),
  });
  if (columnHit) {
    return {
      ...base,
      database: routedDatabase(
        columnHit.explicitDatabase,
        tabDatabase,
        columnHit.binding.relation.qualified.database,
      ),
      identifier: columnHit.binding.column,
      identifierKind: 'column',
      relation: columnHit.binding.relation.qualified,
    };
  }

  // Slice 25: cursor sits on a bare table/view name (no column dot-suffix).
  // The OE-tree lookup discriminates table-vs-view by walking the tree.
  const relationHit = findTableViewHitAtCursor(source, offset, {
    engineId: deps.engineId,
    connectionId,
    database,
  });
  const relationBinding = relationHit?.binding;
  if (relationBinding && (relationBinding.kind === 'table' || relationBinding.kind === 'view')) {
    return {
      ...base,
      // Same cross-database rule as the column case above.
      database: routedDatabase(
        relationHit?.explicitDatabase,
        tabDatabase,
        relationBinding.target.database,
      ),
      identifier: relationBinding.target,
      identifierKind: relationBinding.kind,
    };
  }

  // Slice 26: cursor sits on a routine call (procedure / function). The OE
  // tree owns stored-procedure + function nodes.
  const routineHit = findRoutineBindingAtCursor(source, offset, {
    engineId: deps.engineId,
    connectionId,
    database,
  });
  if (routineHit) {
    return {
      ...base,
      // Same cross-database rule as the column case above.
      database: routedDatabase(
        routineHit.explicitDatabase,
        tabDatabase,
        routineHit.binding.target.database,
      ),
      identifier: routineHit.binding.target,
      identifierKind: 'routine',
    };
  }

  return null;
}

/** Which database's identifier index answers for a hit: the one the chain
 *  SPELLED OUT — in the tree's catalog spelling, which is the identity the
 *  lifecycle keys by — else the tab's. `resolved` (where the OE tree happened
 *  to find the object) stands in only when the surface has no database bound
 *  at all, where the alternative is an empty name that matches no index.
 *
 *  Exported because RENAME routes on the very same question and must answer it
 *  identically: `rename/renameProvider.ts` plans, indexes AND executes against
 *  the database this returns, so a rename typed as `B.dbo.users` from an `A`
 *  tab runs its ALTER in `B` (the MSSQL emitter's `sp_rename '[dbo].[users]'`
 *  carries no database — the connection-level routing is what decides which
 *  server-side object it renames). Two copies of this policy would drift, and
 *  a drift here renames the wrong object. */
export function routedDatabase(
  explicitDatabase: string | undefined,
  tabDatabase: string,
  resolvedDatabase: string | undefined,
): string {
  return explicitDatabase ?? (tabDatabase || resolvedDatabase) ?? '';
}
